import { MermaidRenderer } from '@/catalog-apps/components/component-library/MdTextComponent/MermaidRenderer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useLocalStorage } from '@/hooks'
import { getMarkdownContent, useGetTermsMarkdown } from '@/pages/Applications/AppTermsPage/useGetTerms'
import { useMemo } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const TERMS_ACCEPTANCE_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000

type TermsAcceptanceDialogProps = {
  appId: number | undefined
  appKey: string | undefined
  projectId: number | string | undefined
  workspaceId: number | string | undefined
}

export function TermsAcceptanceDialog({ appId, appKey, projectId, workspaceId }: TermsAcceptanceDialogProps) {
  const { data } = useGetTermsMarkdown(appId)
  const termsContent = getMarkdownContent(data)
  const storageKey = useMemo(
    () => `terms-accepted-at/${appKey ?? 'unknown-app'}/${projectId ?? 'unknown-project'}/${workspaceId ?? 'unknown-workspace'}`,
    [appKey, projectId, workspaceId]
  )
  const [acceptedAt, setAcceptedAt, , isMounted] = useLocalStorage(storageKey, 0)

  // TODO: Include a backend-provided terms version/hash in this key or record when terms storage is available.
  // That will force users to re-accept whenever the published legal text changes, instead of only every 30 days.
  const shouldRequestAcceptance =
    termsContent.trim() !== '' && isMounted && Date.now() - acceptedAt > TERMS_ACCEPTANCE_EXPIRY_MS

  return (
    <AlertDialog open={shouldRequestAcceptance}>
      <AlertDialogContent className="max-h-[85vh] max-w-4xl grid-rows-[auto_minmax(0,1fr)_auto]">
        <AlertDialogHeader>
          <AlertDialogTitle>Terms & Conditions</AlertDialogTitle>
          <AlertDialogDescription>
            Please review and accept the terms before continuing to this application.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ScrollArea className="min-h-0 rounded-md border bg-background px-5 py-4">
          <div className="legal-markdown prose prose-sm max-w-none dark:prose-invert">
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                pre({ children, ...props }) {
                  const codeElement = children as React.ReactElement

                  if (codeElement?.props?.className?.includes('language-mermaid')) {
                    return <MermaidRenderer chart={String(codeElement.props.children).replace(/\n$/, '')} />
                  }

                  return <pre {...props}>{children}</pre>
                },
                code({ className, children, ...props }) {
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  )
                },
              }}
            >
              {termsContent}
            </Markdown>
          </div>
        </ScrollArea>

        <AlertDialogFooter>
          <AlertDialogAction onClick={() => setAcceptedAt(Date.now())}>Accept Terms</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
